/* PTI Scientific Research & Articles directory: collapsed category lists and topic search.
   Built as a custom element so theme-editor section reloads create a fresh instance
   instead of stacking event listeners. */
(() => {
  if (customElements.get('pti-research-directory')) return;

  const normalize = (value) => (value || '').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
  const format = (template, values) =>
    (template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  class PtiResearchDirectory extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.initialCount = Math.max(1, parseInt(this.dataset.initialCount, 10) || 8);
      this.searchPanel = this.querySelector('[data-pti-rd-search]');
      this.form = this.querySelector('[data-pti-rd-form]');
      this.input = this.querySelector('[data-pti-rd-input]');
      this.status = this.querySelector('[data-pti-rd-status]');
      this.empty = this.querySelector('[data-pti-rd-empty]');
      this.emptyTerm = this.querySelector('[data-pti-rd-empty-term]');
      this.clearButton = this.querySelector('[data-pti-rd-clear]');
      this.term = '';

      this.categories = Array.from(this.querySelectorAll('[data-pti-rd-category]'), (element) => {
        const category = {
          element,
          title: normalize(element.dataset.title),
          items: Array.from(element.querySelectorAll('[data-pti-rd-item]'), (item) => ({
            element: item,
            title: normalize(item.dataset.title),
          })),
          toggle: element.querySelector('[data-pti-rd-toggle]'),
          expanded: false,
        };
        if (category.toggle) {
          category.toggle.addEventListener('click', () => this.toggleCategory(category));
        }
        return category;
      });

      if (this.form && this.input) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.search(true);
        });
        this.input.addEventListener('input', () => {
          clearTimeout(this.searchTimer);
          this.searchTimer = setTimeout(() => this.search(), 200);
        });
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.input.value) {
            event.preventDefault();
            this.clear();
          }
        });
        this.searchPanel.hidden = false;
      }

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          this.clear();
          if (this.input) this.input.focus();
        });
      }

      this.render();
    }

    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      clearTimeout(this.statusTimer);
    }

    search(force = false) {
      clearTimeout(this.searchTimer);
      const term = normalize(this.input.value);
      if (term === this.term && !force) return;
      this.term = term;
      this.render();
      this.announce();
    }

    clear() {
      this.input.value = '';
      this.search();
    }

    toggleCategory(category) {
      category.expanded = !category.expanded;
      this.renderCategory(category);
      if (!category.expanded) category.toggle.scrollIntoView({ block: 'nearest' });
    }

    render() {
      let categoryCount = 0;
      let linkCount = 0;

      this.categories.forEach((category) => {
        const shown = this.renderCategory(category);
        if (!category.element.hidden) {
          categoryCount += 1;
          linkCount += shown;
        }
      });

      this.results = { categoryCount, linkCount };

      if (this.empty) {
        this.empty.hidden = !this.term || categoryCount > 0;
        if (this.emptyTerm) this.emptyTerm.textContent = this.input ? this.input.value.trim() : '';
      }
    }

    renderCategory(category) {
      const { items, toggle } = category;
      let shown = 0;

      if (this.term) {
        // A matching category title shows all of its links; otherwise only matching links.
        const titleMatches = category.title.includes(this.term);
        items.forEach((item) => {
          const match = titleMatches || item.title.includes(this.term);
          item.element.hidden = !match;
          if (match) shown += 1;
        });
        category.element.hidden = !titleMatches && shown === 0;
        if (toggle) toggle.hidden = true;
        return shown;
      }

      items.forEach((item, index) => {
        const visible = category.expanded || index < this.initialCount;
        item.element.hidden = !visible;
        if (visible) shown += 1;
      });
      category.element.hidden = false;

      if (toggle) {
        toggle.hidden = items.length <= this.initialCount;
        toggle.setAttribute('aria-expanded', String(category.expanded));
        const label = toggle.querySelector('[data-pti-rd-toggle-label]');
        if (label) {
          label.textContent = category.expanded ? this.dataset.textShowFewer : this.dataset.textShowMore;
        }
      }
      return shown;
    }

    announce() {
      if (!this.status) return;
      const { categoryCount, linkCount } = this.results;
      const term = this.input.value.trim();
      let message;

      if (!this.term) {
        message = this.dataset.textCleared;
      } else if (!categoryCount) {
        message = format(this.dataset.textNoResults, { term });
      } else {
        const links = format(linkCount === 1 ? this.dataset.textLinkOne : this.dataset.textLinkOther, {
          count: linkCount,
        });
        const categories = format(
          categoryCount === 1 ? this.dataset.textCategoryOne : this.dataset.textCategoryOther,
          { count: categoryCount }
        );
        message = format(this.dataset.textResults, { links, categories, term });
      }

      // Clearing first makes screen readers repeat an identical message.
      this.status.textContent = '';
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        this.status.textContent = message;
      }, 100);
    }
  }

  customElements.define('pti-research-directory', PtiResearchDirectory);
})();
